import {
  ReactElement,
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  ComponentPropsWithoutRef,
  useId,
  useState,
  useRef,
  useMemo,
} from "react";
import cx from "classnames";
import { Label, LabelProps } from "../Label";
import { Spinner } from "../Spinner";
import styles from "./TextField.module.scss";
import { Helper, HelperProps } from "../../internal/components";
import { LayoutUtilProps, Size, FieldLabelProps } from "../../types";
import {
  useLayoutPropsUtil,
  useMergeRefs,
  useOptionallyControlledState,
} from "../../hooks";

export type TextFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "size" | "prefix"
> &
  LayoutUtilProps &
  FieldLabelProps & {
    error?: ReactElement | string | boolean;
    label?: LabelProps["children"];
    prefix?: string | ReactElement;
    suffix?: string | ReactElement;
    size?: Extract<Size, "small" | "medium" | "large">;
    type?: "text" | "email" | "tel" | "url" | "password" | "number";
    description?: HelperProps["description"];
    errorAriaLive?: HelperProps["errorAriaLive"];
    hint?: HelperProps["hint"];
    loading?: boolean;
    disabled?: boolean;
  } & TextareaConditionalProps;

type TextareaConditionalProps =
  | {
      showCounter: true;
      maxLength: HelperProps["maxLength"];
    }
  | {
      showCounter?: false;
      maxLength?: HelperProps["maxLength"];
    };

export const TextFieldElement = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      error,
      errorAriaLive: errorAriaLiveProp = "assertive",
      defaultValue: defaultValueProp,
      value: valueProp,
      label,
      moreInfo,
      prefix,
      suffix,
      size,
      type = "text",
      required,
      onChange,
      onFocus,
      onBlur,
      id: idProp,
      description,
      hint,
      showCounter,
      maxLength,
      loading,
      disabled,
      labelProps,
      style,
      placeholder,
      ...rest
    } = componentProps;

    const inputRef = useRef<HTMLInputElement>(null);
    const [isTyping, setIsTyping] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const [errorAriaLive, setErrorAriaLive] =
      useState<HelperProps["errorAriaLive"]>("off");

    // Use the optionally controlled state for value
    const [value, setValue] = useOptionallyControlledState<typeof valueProp>({
      controlledValue: valueProp,
      defaultValue: defaultValueProp,
    });

    const length = useMemo(() => {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        Array.isArray(value)
      ) {
        return String(value).length;
      }
      return 0;
    }, [value]);

    const onChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
      setValue(e.target.value);
      onChange?.(e);
      if (!isTyping) {
        setIsTyping(true);
      }
    };

    const focusHandler: FocusEventHandler<HTMLInputElement> = (e) => {
      onFocus?.(e);
      setOpenInfo(true);
      setErrorAriaLive(errorAriaLiveProp);
    };

    const blurHandler: FocusEventHandler<HTMLInputElement> = (e) => {
      onBlur?.(e);
      setOpenInfo(false);
      setErrorAriaLive("off");
      if (isTyping) {
        setIsTyping(false);
      }
    };

    const labelUid = useId();
    const id = idProp ?? labelUid;

    const prefixUid = useId();
    const suffixUid = useId();

    const helperUid = useId();

    const errorMessage = typeof error !== "boolean" ? error : undefined;

    const styleCombined = { ...style, ...layoutStyles };

    return (
      <div
        className={cx(styles["textfield"], className)}
        data-anv="textfield"
        style={styleCombined}
      >
        {label && (
          <Label
            moreInfo={moreInfo}
            openMoreInfo={openInfo}
            required={required}
            htmlFor={id}
            {...labelProps}
          >
            {label}
          </Label>
        )}
        <div
          className={cx(styles["input-wrapper"], {
            [styles["small"]]: size === "small",
            [styles["large"]]: size === "large",
          })}
          role="presentation"
          onClick={() => inputRef.current?.focus()}
        >
          {prefix ? (
            <div
              aria-hidden
              className={styles["prefix"]}
              id={`prefix${prefixUid}`}
            >
              {prefix}
            </div>
          ) : null}
          <input
            id={id}
            className={cx(styles["input"], {
              [styles["error"]]: error,
            })}
            onChange={onChangeHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
            ref={useMergeRefs([ref, inputRef])}
            required={required}
            maxLength={maxLength}
            placeholder={placeholder}
            type={type}
            disabled={disabled}
            value={value ?? ""}
            {...rest}
            aria-label={label ? undefined : placeholder}
            aria-describedby={helperUid}
            aria-invalid={error ? !!error : undefined}
          />
          {loading ? (
            <div className={styles["loading-spinner-wrapper"]}>
              <Spinner />
            </div>
          ) : null}
          {suffix ? (
            <div
              className={styles["suffix"]}
              aria-hidden
              id={`suffix${suffixUid}`}
            >
              {suffix}
            </div>
          ) : null}
        </div>
        {hint || showCounter || errorMessage || description ? (
          <Helper
            id={helperUid}
            hint={hint}
            maxLength={maxLength}
            inputLength={length}
            isTyping={isTyping}
            showCounter={showCounter}
            errorMessage={errorMessage}
            errorAriaLive={errorAriaLive}
            description={description}
          />
        ) : null}
      </div>
    );
  },
);

export const TextField = Object.assign(TextFieldElement, {});
